import { useEffect, useState } from 'react'

import { Box, Container, Grid } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { BusinessTopicType } from 'constants/enums/TopicTypeEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import {
	guardUnsavedAction,
	useUnsavedIdeationWarning,
} from 'utils/useUnsavedIdeationWarning'

import {
	BuildingBlocksModel,
	updateAIResponse,
	userMessage,
} from 'models/BuildingBlocksModel'
import { Game } from 'models/gameModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import {
	postBuildingBlocksAdditionalContextPrompt,
	postBuildingBlocksPrompt,
} from 'services/completions.service'
import { postIdea } from 'services/ideas.service'

import BreadCrumbs from 'components/BreadCrumbs'
import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Hero from '../../components/Hero/Hero'
import { styles } from './BuildingBlocks.styles'
import Section1 from './components/Section1/Section1'
import Section2 from './components/Section2/Section2'
import Section3 from './components/Section3/Section3'

export const TEST_ID = 'building-blocks'

const BuildingBlocks = ({ game }: { game?: Game }) => {
	useEffect(() => {
		if (game) {
			setInnovationCompany(game.innovationCompany)
			setInnovationTopic(game.innovationTopic)
			setCompanyProblem(game.companyProblem)
			setGameId(game.gameId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const {
		innovationCompany,
		setInnovationCompany,
		companyProblem,
		setCompanyProblem,
		authorIdeation,
		setAuthorIdeation,
		innovationTopic,
		setInnovationTopic,
		setGameId,
		generateScenarioDisabled,
		termsAgreed,
		setTermsAgreed,
		updateCompanyAdvice,
		submitIdeasDisabled,
		promptToJSON,
		setPromptResponse,
		buildingBlocksPrompt,
		gameToJSON,
		reset,
		promptConversation,
		setPromptConversation,
		resetSoft,
	} = BuildingBlocksModel()

	const { showAlert } = useToastContext()
	const { setIdeaStreakCount } = useIdeaStreakContext()

	const [isPromptLoading, setIsPromptLoading] = useState(false)
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)
	const [additionalContextResponse, setAdditionalContextResponse] = useState<
		string | undefined
	>()
	const [isAdditionalContextLoading, setIsAdditionalContextLoading] =
		useState(false)
	const { showModal } = useModalContext()
	const isUnsaved = hasUnsavedIdeation(step, authorIdeation)

	useUnsavedIdeationWarning(isUnsaved, authorIdeation, showModal)

	const resetAllField = () => {
		setInnovationCompany('')
		setInnovationTopic('')
		setCompanyProblem('')
	}

	const refreshPrompt = async () => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				if (refreshClicksSinceIdeation < 2) {
					await createPrompt()
				}

				setRefreshClicksSinceIdeation((prev) => prev + 1)
			},
		})
	}

	const guardedUpdateCompanyAdvice = async () => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: () => updateCompanyAdvice(),
		})
	}

	const loadAdditionalContext = async () => {
		if (!buildingBlocksPrompt?.data) return
		try {
			setIsAdditionalContextLoading(true)
			const response = await postBuildingBlocksAdditionalContextPrompt({
				innovationTopic,
				innovationCompany,
				companyProblem,
				innovationIdeas: buildingBlocksPrompt.data,
				conversation: [userMessage],
			})
			if (response.status === 200 && typeof response.data === 'string') {
				setAdditionalContextResponse(response.data)
			}
		} catch (error) {
			logger.error(error)
		} finally {
			setIsAdditionalContextLoading(false)
		}
	}

	const createPrompt = async () => {
		try {
			setIsPromptLoading(true)
			resetSoft()
			setAdditionalContextResponse(undefined)

			// setup the conversation
			const prompt = promptToJSON()
			prompt.conversation = [...promptConversation, userMessage]
			setStep(GAME_STEP.Step2)
			window.scrollTo(0, 0)
			const response = await postBuildingBlocksPrompt(prompt)

			// setup history
			if (response.status === 200 && response.data) {
				if (typeof response.data !== 'string') {
					throw Error(
						`invalid response type. expected: string, actual: ${typeof response.data}`
					)
				}

				const msg = JSON.stringify(response.data)
				// add response to state
				setPromptConversation((prevMessages) => [
					...prevMessages,
					userMessage,
					updateAIResponse(msg),
				])
				setPromptResponse(response)
			}
		} catch (error) {
			logger.error(error)
			showAlert?.({
				title: defaultErrorMessage.title,
				bodyText: defaultErrorMessage.bodyText,
				severity: ToastSeverity.ERROR,
			})
		} finally {
			setIsPromptLoading(false)
		}
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(gameToJSON())
			if (response.status !== 200) {
				throw new Error(
					`Failed to submit idea. Status: ${response.status}`
				)
			}

			showAlert?.({
				severity: ToastSeverity.SUCCESS,
			})

			setIdeaStreakCount((prevState) => prevState + 1)
			setRefreshClicksSinceIdeation(0)
			// need to reset the model
			reset()
			window.scrollTo({
				top: 0,
			})

			// Load new prompt
			createPrompt()
		} catch (error) {
			logger.error(error)
			showAlert?.({
				title: alertTitleTextMap.submitIdeaError.title,
				bodyText: alertTitleTextMap.submitIdeaError.text,
				severity: ToastSeverity.ERROR,
			})
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				step={step}
				gameTypeEnum={GAME_TYPE.BUILDING_BLOCKS}
				innovationTopic={innovationTopic as BusinessTopicType}
				innovationCompany={innovationCompany}
				companyProblem={companyProblem}
				isLoading={isPromptLoading || isSubmitButtonLoading}
				setCompanyProblem={setCompanyProblem}
				setInnovationCompany={setInnovationCompany}
				setInnovationTopic={setInnovationTopic}
				createPrompt={createPrompt}
				hideEdit={Boolean(game && (game.gameTitle || game.endTime))}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth={step === GAME_STEP.Step1 ? 'md' : 'lg'}
				sx={styles.container}
			>
				<Box sx={styles.containerBox}>
					<BreadCrumbs
						breadcrumbs={[
							{
								label: '1. INNOVATION TOPIC',
								active: step === GAME_STEP.Step1,
								onClick:
									step !== GAME_STEP.Step1
										? () => setStep(GAME_STEP.Step1)
										: undefined,
							},
							{
								label: '2. FACILITATION TECHNIQUE & 3. IDEATION',
								active: step !== GAME_STEP.Step1,
							},
						]}
					/>
				</Box>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isPromptLoading}
						innovationCompany={innovationCompany}
						setInnovationCompany={setInnovationCompany}
						innovationTopic={innovationTopic as BusinessTopicType}
						setInnovationTopic={setInnovationTopic}
						companyProblem={companyProblem}
						setCompanyProblem={setCompanyProblem}
						generateScenarioDisabled={generateScenarioDisabled}
						resetAllField={resetAllField}
						createPrompt={createPrompt}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<Grid container>
						<Grid item md={6}>
							<Section2
								additionalContextResponse={
									additionalContextResponse
								}
								buildingBlocksPrompt={buildingBlocksPrompt}
								isAdditionalContextLoading={
									isAdditionalContextLoading
								}
								loading={
									isPromptLoading || isSubmitButtonLoading
								}
								createPrompt={refreshPrompt}
								loadAdditionalContext={loadAdditionalContext}
								reloadIdeas={guardedUpdateCompanyAdvice}
							/>
						</Grid>
						<Grid item md={6}>
							<Section3
								submitIdeasDisabled={submitIdeasDisabled}
								loading={
									isPromptLoading || isSubmitButtonLoading
								}
								submitIdeas={handleSubmitIdeas}
								authorIdeation={authorIdeation}
								setAuthorIdeation={setAuthorIdeation}
								viewSubmittedIdeas={viewSubmittedIdeas}
								cautionText={
									game?.gameTitle || game?.endTime
										? brainstormText.global
												.alternateWarningText
										: brainstormText.global.warningText
								}
								termsAgreed={termsAgreed}
								setTermsAgreed={setTermsAgreed}
								gameId={game?.gameId}
							/>
						</Grid>
					</Grid>
				)}
			</Container>
		</Box>
	)
}
export default BuildingBlocks
